<template>
  <main class="kyc-webview-page">
    <AppNavBar
      :title="t('card.diditSdkTitle')"
      :auto-back="false"
      fallback-to="/home/card"
      @click-left="handleBack"
    />

    <section class="sdk-stage">
      <div class="sdk-card" :class="{ 'sdk-card--error': Boolean(sdkErrorMessage) }">
        <div data-strong>{{ sdkTitle }}</div>
        <p>{{ sdkMessage }}</p>

        <div class="sdk-actions">
          <van-button
            v-if="showRetry"
            round
            type="primary"
            class="sdk-actions__primary"
            :loading="sdkLoading"
            @click="startVerification"
          >
            {{ t('card.restartVerification') }}
          </van-button>

          <van-button
            v-if="verificationUrl"
            round
            plain
            type="primary"
            class="sdk-actions__secondary"
            @click="openExternal"
          >
            {{ t('card.openVerificationPage') }}
          </van-button>

          <van-button
            v-if="showCheckStatus"
            round
            plain
            type="primary"
            class="sdk-actions__secondary"
            :loading="checkingStatus"
            @click="checkStatus"
          >
            {{ t('card.checkVerificationStatus') }}
          </van-button>
        </div>
      </div>
    </section>
  </main>
</template>

<script setup>
import { computed, onBeforeUnmount, onMounted, ref } from 'vue';
import { closeToast, showFailToast, showLoadingToast } from 'vant';
import { useI18n } from 'vue-i18n';
import { useRoute, useRouter } from 'vue-router';
import { DiditSdk } from '@didit-protocol/sdk-web';
import { resolveDiditVerificationUrl } from '@/view/card/utils';
import { useCardKycStore } from '@/stores/card-kyc';
import { useProfileStore } from '@/stores/profile';
import { getRouteState, navigateWithState } from '@/utils/route-state';
import { safeBack } from '@/utils/navigation';

const { t } = useI18n();
const route = useRoute();
const router = useRouter();
const profileStore = useProfileStore();
const cardKycStore = useCardKycStore();
const pageState = computed(() => getRouteState(route, 'card-kyc-webview', {}));

const verificationUrl = computed(() => resolveDiditVerificationUrl({
  url: pageState.value.url || '',
  token: pageState.value.token || ''
}));

const sdkLoading = ref(false);
const sdkErrorMessage = ref('');
const sdkStarted = ref(false);
const checkingStatus = ref(false);

let diditSdk = null;

const showRetry = computed(() => !sdkStarted.value);
const showCheckStatus = computed(() => Boolean(sdkErrorMessage.value) || cardKycStore.diditSessionStatus === 'Pending');
const sdkTitle = computed(() => (
  sdkLoading.value
    ? t('card.sdkLoading')
    : sdkErrorMessage.value
      ? t('card.sdkUnavailable')
      : t('card.sdkReady')
));
const sdkMessage = computed(() => {
  if (!verificationUrl.value) {
    return t('card.verificationPageUnavailable');
  }
  if (sdkErrorMessage.value) {
    return sdkErrorMessage.value;
  }
  if (sdkLoading.value) {
    return t('card.sdkLoading');
  }
  return t('card.openVerificationPage');
});

function cleanupSdk({ reset = false } = {}) {
  try {
    diditSdk?.close?.();
  } catch (error) {
    // ignore
  }
  try {
    diditSdk?.destroy?.();
  } catch (error) {
    // ignore
  }
  diditSdk = null;
  if (reset) {
    try {
      DiditSdk.reset();
    } catch (error) {
      // ignore
    }
  }
}

function handleBack() {
  cleanupSdk({ reset: true });
  router.replace('/home/card');
}

function openExternal() {
  if (!verificationUrl.value) {
    return;
  }
  window.open(verificationUrl.value, '_blank', 'noopener');
}

async function checkStatus() {
  if (!profileStore.platformAddress) {
    router.replace('/login');
    return;
  }

  checkingStatus.value = true;
  showLoadingToast({ message: t('common.loading'), forbidClick: true, duration: 0 });
  try {
    const result = await cardKycStore.refreshStatus({ address: profileStore.platformAddress });
    navigateWithState(router, '/card/kyc/result', {
      namespace: 'card-kyc-result',
      replace: true,
      payload: {
        holderStatus: String(result?.holderStatus || '2'),
        reason: result?.reason || ''
      }
    });
  } catch (error) {
    showFailToast(error.message || t('common.retryLater'));
  } finally {
    checkingStatus.value = false;
    closeToast();
  }
}

async function refreshStatusAndRoute({ fallbackStatus = '2', fallbackReason = '' } = {}) {
  if (!profileStore.platformAddress) {
    router.replace('/login');
    return;
  }

  try {
    const result = await cardKycStore.refreshStatus({ address: profileStore.platformAddress });
    navigateWithState(router, '/card/kyc/result', {
      namespace: 'card-kyc-result',
      replace: true,
      payload: {
        holderStatus: String(result?.holderStatus || fallbackStatus),
        reason: result?.reason || fallbackReason
      }
    });
  } catch (error) {
    navigateWithState(router, '/card/kyc/result', {
      namespace: 'card-kyc-result',
      replace: true,
      payload: {
        holderStatus: String(fallbackStatus),
        reason: fallbackReason
      }
    });
  }
}

function bindDiditCallbacks() {
  if (!diditSdk) {
    return;
  }

  diditSdk.onStateChange = (state, error) => {
    cardKycStore.setDiditState(state || 'idle', state === 'error' ? error : '');
    if (state === 'error') {
      sdkErrorMessage.value = error || t('card.sdkUnavailable');
      sdkLoading.value = false;
      sdkStarted.value = false;
    }
  };

  diditSdk.onEvent = (event) => {
    cardKycStore.setDiditEvent(event);
  };

  diditSdk.onComplete = async (result) => {
    cardKycStore.setDiditCompletion(result);
    sdkLoading.value = false;
    sdkStarted.value = false;

    switch (result?.type) {
      case 'completed': {
        const status = String(result?.session?.status || '');
        if (status === 'Approved' || status === 'Pending') {
          navigateWithState(router, '/card/kyc/result', {
            namespace: 'card-kyc-result',
            replace: true,
            payload: { holderStatus: '2', reason: '' }
          });
          break;
        }
        await refreshStatusAndRoute({
          fallbackStatus: '1',
          fallbackReason: t('card.sdkDeclined')
        });
        break;
      }
      case 'cancelled':
        sdkErrorMessage.value = '';
        break;
      case 'failed':
        sdkErrorMessage.value = result?.error?.message || t('card.sdkUnavailable');
        break;
      default:
        break;
    }
  };
}

async function startVerification() {
  if (!verificationUrl.value) {
    sdkErrorMessage.value = t('card.verificationPageUnavailable');
    return;
  }

  sdkLoading.value = true;
  sdkErrorMessage.value = '';
  sdkStarted.value = true;
  cardKycStore.resetDiditRuntime();
  cleanupSdk({ reset: true });

  try {
    diditSdk = DiditSdk.shared;
    bindDiditCallbacks();
    await diditSdk.startVerification({
      url: verificationUrl.value,
      configuration: {
        loggingEnabled: true,
        // closeModalOnComplete: true,
        // showExitConfirmation: true,
        showCloseButton: false
      }
    });
  } catch (error) {
    sdkErrorMessage.value = error?.message || t('card.sdkUnavailable');
    sdkStarted.value = false;
  } finally {
    sdkLoading.value = false;
  }
}

onMounted(() => {
  cardKycStore.setDiditState('idle');
  void startVerification();
});

onBeforeUnmount(() => {
  cleanupSdk({ reset: true });
});
</script>

<style scoped lang="scss">
.kyc-webview-page {
  display: flex;
  flex-direction: column;
  min-height: var(--app-viewport-height);
  background: #f7f8fa;
}

.sdk-stage {
  flex: 1;
  padding: 12px 12px 0;
}

.sdk-card {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  min-height: calc(var(--app-viewport-height) - 172px);
  padding: 24px;
  border-radius: 16px;
  background: #ffffff;
  box-shadow: 0 8px 24px rgba(31, 76, 169, 0.08);
  text-align: center;

  &--error {
    background: #fffafa;
  }

  [data-strong] {
    margin-top: 14px;
    color: #1f2430;
    font-size: 17px;
    font-weight: 500;
  }

  p {
    margin: 10px 0 0;
    color: #667085;
    font-size: 14px;
    line-height: 22px;
  }
}

.sdk-actions {
  display: flex;
  flex-direction: column;
  gap: 10px;
  width: 100%;
  max-width: 280px;
  margin-top: 20px;
}

.sdk-actions__primary,
.sdk-actions__secondary {
  width: 100%;
}
</style>
